import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useState } from 'react';
import { useMe } from '@/data/user';
import { loadStripe } from '@stripe/stripe-js';
import { useMutation } from 'react-query';
import { toast } from 'react-hot-toast';
import { Check, X, CreditCard, RefreshCw } from 'lucide-react';
import { useRouter } from 'next/router';
import Client from '@/data/client';
import Image from 'next/image';
import Pbucks from '@/assets/images/P-Bucks.svg';
import { useAuthAction } from '@/hooks/use-auth-action';

const stripePromise = loadStripe(
  process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY as string
);

interface PricingPlansProps {
  buttonLabel?: string;
  activeTab?: string;
  onTabChange?: (tab: string) => void;
  variant?: 'full' | 'subscription' | 'credits';
}

interface Plan {
  name: string;
  monthlyPrice: number;
  yearlyPrice: number;
  description: string;
  features: (string | JSX.Element)[];
  popular?: boolean;
}

interface CreditPack {
  name: string;
  credits: number;
  price: number;
  standardImages: number;
  aiModels: number;
  features: (string | JSX.Element)[];
  popular?: boolean;
}

export function PricingPlans({
  buttonLabel = 'Get Started',
  activeTab = 'subscription',
  onTabChange,
  variant = 'full',
}: PricingPlansProps) {
  const [billingCycle, setBillingCycle] = useState('month');
  const { me, currentPlan, currentCycle, hasSubscription } = useMe();
  const router = useRouter();
  const { handleAuthAction } = useAuthAction();

  const creditPacks: CreditPack[] = [
    {
      name: '5,000 P-Bucks',
      credits: 5000,
      price: 6,
      standardImages: 24,
      aiModels: 1,
      features: ['Full, commercial ownership of images'],
    },
    {
      name: '10,000 P-Bucks',
      credits: 10000,
      price: 12,
      standardImages: 48,
      aiModels: 2,
      features: ['Full, commercial ownership of images'],
    },
    {
      name: '20,000 P-Bucks',
      credits: 20000,
      price: 24,
      standardImages: 96,
      aiModels: 5,
      features: ['Full, commercial ownership of images'],
    },
    {
      name: '40,000 P-Bucks',
      credits: 40000,
      price: 48,
      standardImages: 192,
      aiModels: 11,
      features: ['Full, commercial ownership of images'],
    },
  ];

  const plans: Plan[] = [
    {
      name: 'Starter',
      monthlyPrice: 16,
      yearlyPrice: 160,
      description: '44 Standard Images or \n3 Personal AI Models \na month',
      features: [
        <div key="images" className="flex items-center gap-2">
          <Image
            src={Pbucks}
            width={16}
            height={16}
            alt="P-Bucks"
            className="h-4 w-4"
            priority
            loading="eager"
            unoptimized
          />
          <span>11,000 monthly credits</span>
        </div>,
        'Rollover up to 1,100 unused credits with renewal',
        '20% discount on top-up credits',
        'Full, commercial ownership of images',
        'Highest quality AI models',
        'Cancel anytime',
      ],
    },
    {
      name: 'Creator',
      monthlyPrice: 49,
      yearlyPrice: 490,
      description: '136 Standard Images or \n9 Personal AI Models\n a month',
      features: [
        <div key="images" className="flex items-center gap-2">
          <Image
            src={Pbucks}
            width={16}
            height={16}
            alt="P-Bucks"
            className="h-4 w-4"
            priority
            loading="eager"
            unoptimized
          />
          <span>34,000 monthly credits</span>
        </div>,
        'Rollover up to 3,400 unused credits with renewal',
        '20% discount on top-up credits',
        'Full, commercial ownership of images',
        'Highest quality AI models',
        'Cancel anytime',
      ],
      popular: true,
    },
    {
      name: 'Professional',
      monthlyPrice: 99,
      yearlyPrice: 990,
      description: '296 Standard Images or \n21 Personal AI Models \na month',
      features: [
        <div key="images" className="flex items-center gap-2">
          <Image
            src={Pbucks}
            width={16}
            height={16}
            alt="P-Bucks"
            className="h-4 w-4"
            priority
            loading="eager"
            unoptimized
          />
          <span>74,000 monthly credits</span>
        </div>,
        'Rollover up to 7,400 unused credits with renewal',
        '20% discount on top-up credits',
        'Full, commercial ownership of images',
        'Highest quality AI models',
        'Priority support',
        'Cancel anytime',
      ],
    },
  ];

  const handleManageSubscription = async () => {
    try {
      const response = await Client.stripe.createPortalSession({
        return_url: window.location.href,
      });
      window.location.href = response.url;
    } catch (error) {
      toast.error('Failed to open subscription management portal');
    }
  };

  const {
    mutate: createSubscriptionCheckout,
    isLoading: isLoadingSubscription,
  } = useMutation(
    async ({ planName, cycle }: { planName: string; cycle: string }) => {
      if (hasSubscription) {
        handleManageSubscription();
      } else {
        const stripe = await stripePromise;
        const response = await Client.stripe.createSubscriptionSession({
          planName,
          cycle,
          email: me?.email,
          cancelPath: window.location.pathname,
          successPath: window.location.pathname,
          return_url: window.location.pathname,
        });
        if (response.id) {
          const result = await stripe?.redirectToCheckout({
            sessionId: response.id,
          });
          if (result?.error) {
            throw new Error(result.error.message);
          }
        } else if (response.url) {
          window.open(response.url, '_blank');
        }
      }
    },
    {
      onError: (error: Error) => {
        toast.error(`Error: ${error.message}`);
      },
    }
  );

  const { mutate: purchaseCredits, isLoading: isLoadingCredits } = useMutation(
    async ({ packName, amount }: { packName: string; amount: number }) => {
      const stripe = await stripePromise;
      const response = await Client.stripe.createCreditsSession({
        packName,
        amount,
        email: me?.email,
        cancelPath: '/billing',
        successPath: router.asPath,
      });
      const result = await stripe?.redirectToCheckout({
        sessionId: response.id,
      });
      if (result?.error) {
        throw new Error(result.error.message);
      }
    },
    {
      onError: (error: Error) => {
        toast.error(`Error: ${error.message}`);
      },
    }
  );

  const handleSubscriptionSelect = (planName: string, cycle: string) => {
    handleAuthAction(() => {
      createSubscriptionCheckout({ planName, cycle });
    });
  };

  const handleCreditsSelect = (packName: string, amount: number) => {
    handleAuthAction(() => {
      purchaseCredits({ packName, amount });
    });
  };

  function PricingCard({
    title,
    price,
    description,
    features,
    buttonText,
    type,
    onClick,
    isLoading,
    highlighted = false,
    cycle,
  }: {
    title: string;
    price: string | number;
    description: string;
    features: (string | JSX.Element)[];
    buttonText: string;
    type?: string;
    onClick?: () => void;
    isLoading?: boolean;
    highlighted?: boolean;
    cycle?: string;
  }) {
    const isCurrentPlan =
      title === currentPlan?.replace(' Plan', '') &&
      cycle === currentCycle &&
      billingCycle === currentCycle;

    return (
      <div
        className={`relative flex flex-col rounded-lg p-6 ${
          highlighted
            ? 'bg-card dark:bg-card/50 border-2 border-brand shadow-xl dark:border-brand-dark'
            : 'border-border dark:border-border dark:bg-card/25 border bg-background/50'
        } text-foreground duration-300 transition-all hover:shadow-lg`}
      >
        {highlighted && (
          <div className="absolute -top-3 left-0 flex w-full justify-center">
            <span className="rounded-full bg-brand px-4 py-1 text-sm font-semibold text-white dark:bg-brand-dark">
              Recommended
            </span>
          </div>
        )}
        <div className="flex-grow space-y-6">
          <div className="relative space-y-4">
            <div className="absolute -left-2 top-0 h-full w-0.5 rounded-full bg-gradient-to-b from-brand/50 to-brand/20 dark:from-brand-dark/50 dark:to-brand-dark/20"></div>
            <div className="space-y-2">
              <span className="inline-block rounded-md bg-brand/10 px-2 py-1 text-xs font-medium uppercase tracking-wider text-brand dark:bg-brand-dark/10 dark:text-brand-dark">
                {title}
              </span>
              <p className="whitespace-pre-line text-lg font-medium leading-relaxed text-foreground">
                {description}
              </p>
            </div>
          </div>
          <p className="text-4xl font-bold">
            ${billingCycle === 'month' ? price : Math.round(Number(price) / 12)}
            <span className="text-muted-foreground text-sm">
              {type !== 'credits' ? '/mo' : ' valid for 1 year'}
            </span>
          </p>
          {billingCycle === 'year' && type !== 'credits' && (
            <div className="text-muted-foreground flex items-center gap-2 text-sm">
              <span>${price} billed yearly</span>
              <span className="rounded-md bg-red-500/10 px-2 py-0.5 text-xs font-medium text-red-500">
                Save 2 Months
              </span>
            </div>
          )}
          <ul className="space-y-3">
            {features.map((feature, index) => (
              <li key={index} className="flex items-center gap-2">
                <Check className="flex-shrink-0 text-green-500" size={16} />
                <span className="text-sm">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-6">
          <Button
            onClick={onClick}
            disabled={isLoading}
            className={`w-full ${
              isCurrentPlan
                ? '!text-destructive-foreground hover:bg-destructive/90 cursor-default !bg-red-500'
                : highlighted
                ? 'bg-brand text-white hover:bg-brand/90 dark:bg-brand-dark dark:hover:bg-brand-dark/90'
                : 'bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600'
            }`}
          >
            {isLoading
              ? 'Processing...'
              : isCurrentPlan
              ? 'Current Plan'
              : currentPlan && currentPlan !== 'Free Plan' && type !== 'credits'
              ? 'Change Plan'
              : type !== 'credits'
              ? 'Upgrade Plan'
              : buttonText}
          </Button>
        </div>
      </div>
    );
  }

  function CreditPackCard({
    credits,
    price,
    standardImages,
    aiModels,
    onClick,
    isLoading,
  }: {
    credits: number;
    price: number;
    standardImages: number;
    aiModels: number;
    onClick?: () => void;
    isLoading?: boolean;
  }) {
    const originalPrice = price;
    const actualPrice = hasSubscription ? price - price * 0.2 : price;
    const hasDiscount = hasSubscription;

    return (
      <div className="border-border bg-card/50 dark:bg-card/25 relative flex items-center justify-between rounded-xl border p-4 duration-200 transition-all hover:shadow-md dark:border-gray-800">
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <div className="relative">
              <div className="absolute inset-[-2px] rounded-full bg-gradient-to-br from-blue-500/30 to-blue-600/30 opacity-75 blur-sm"></div>
              <Image
                src={Pbucks}
                width={24}
                height={24}
                alt="P-Bucks"
                className="relative animate-[pulseBorder_2s_infinite] duration-300 transition-transform"
              />
            </div>
            <span className="text-xl font-bold">
              {credits.toLocaleString()} P-Bucks
            </span>
            {hasDiscount && (
              <span className="rounded-full bg-green-500/10 px-2 py-0.5 text-xs font-medium text-green-500">
                20% off for subscribers
              </span>
            )}
          </div>
          <div className="mt-2 flex flex-col gap-1">
            <div className="text-muted-foreground flex items-center gap-2 text-sm">
              <span className="font-medium text-foreground">
                {standardImages}
              </span>
              <span>Standard Images</span>
              <span className="text-muted-foreground mx-2">or</span>
              <span className="font-medium text-foreground">{aiModels}</span>
              <span>Personal AI {aiModels === 1 ? 'Model' : 'Models'}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-8">
          <Button
            onClick={onClick}
            disabled={isLoading}
            className="bg-brand text-white hover:bg-brand/90 dark:bg-brand-dark dark:hover:bg-brand-dark/90"
          >
            {isLoading ? (
              'Processing...'
            ) : (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mr-2"
                >
                  <circle cx="9" cy="21" r="1" />
                  <circle cx="20" cy="21" r="1" />
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                </svg>
                {hasDiscount ? (
                  <div className="flex items-center gap-2">
                    <span className="text-sm line-through opacity-75">
                      ${originalPrice}
                    </span>
                    <span>${actualPrice}</span>
                  </div>
                ) : (
                  `Buy for $${actualPrice}`
                )}
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  // When the tab changes internally, notify the parent component
  const handleTabChange = (tab: string) => {
    if (onTabChange) {
      onTabChange(tab);
    }
  };

  return (
    <div className="w-full">
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="w-full"
      >
        {variant === 'full' && (
          <TabsList className="bg-card/50 mb-8 grid h-auto w-full grid-cols-2 gap-2">
            <TabsTrigger
              value="subscription"
              className="data-[state=inactive]:hover:bg-muted/50 relative overflow-hidden
              py-3
              px-4 
              duration-200
              transition-all 
              data-[state=active]:scale-[1.02]
              data-[state=active]:border-b-2
              data-[state=active]:border-primary
              data-[state=active]:bg-background
              data-[state=inactive]:opacity-70 
              data-[state=active]:shadow-lg"
            >
              <div className="flex flex-col items-center space-y-1.5">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-5 w-5"
                >
                  <path d="M4 9a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3z" />
                  <path d="M8 7V5c0-1.1.9-2 2-2h4a2 2 0 0 1 2 2v2" />
                  <path d="M12 12v.01" />
                  <path d="M16 12v.01" />
                  <path d="M8 12v.01" />
                </svg>
                <span className="text-base font-semibold text-foreground">
                  Subscription
                </span>
                <span className="text-muted-foreground text-xs">
                  Monthly or yearly plans
                </span>
              </div>
            </TabsTrigger>
            <TabsTrigger
              value="credits"
              className="data-[state=inactive]:hover:bg-muted/50 relative overflow-hidden 
              py-3
              px-4 
              duration-200 
              transition-all
              data-[state=active]:scale-[1.02] 
              data-[state=active]:border-b-2
              data-[state=active]:border-primary
              data-[state=active]:bg-background
              data-[state=inactive]:opacity-70 
              data-[state=active]:shadow-lg"
            >
              <div className="flex flex-col items-center space-y-1.5">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-5 w-5"
                >
                  <circle cx="12" cy="12" r="8" />
                  <path d="M14.5 9a2 2 0 0 0-2-2h-1a2 2 0 0 0 0 4h2a2 2 0 0 1 0 4h-1a2 2 0 0 1-2-2" />
                  <path d="M12 6v2m0 8v2" />
                </svg>
                <span className="text-base font-semibold text-foreground">
                  Pay As You Go
                </span>
                <span className="text-muted-foreground text-xs">
                  Top-up credits
                </span>
              </div>
            </TabsTrigger>
          </TabsList>
        )}

        <TabsContent value="subscription">
          <div className="space-y-4">
            <div className="mb-8 flex justify-center">
              <div className="bg-card/50 border-border dark:bg-card/25 relative inline-flex rounded-xl border p-1.5 shadow-sm dark:border-gray-800">
                <div
                  className="dark:bg-muted absolute inset-1.5 rounded-lg bg-background shadow-md duration-200 transition-transform dark:border dark:border-gray-800"
                  style={{
                    transform: `translateX(${
                      billingCycle === 'year' ? '100%' : '0'
                    })`,
                    width: 'calc(50% - 3px)',
                  }}
                />
                <button
                  onClick={() => setBillingCycle('month')}
                  className={`relative rounded-lg px-8 py-3 duration-200 transition-all ${
                    billingCycle === 'month'
                      ? 'scale-105 font-bold text-foreground shadow-sm dark:shadow-none'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <div className="flex flex-col items-center space-y-0.5">
                    <span className="font-semibold">Monthly</span>
                    <span className="text-muted-foreground text-xs">
                      Regular price
                    </span>
                  </div>
                </button>
                <button
                  onClick={() => setBillingCycle('year')}
                  className={`relative rounded-lg px-8 py-3 duration-200 transition-all ${
                    billingCycle === 'year'
                      ? 'scale-105 font-bold text-foreground shadow-sm dark:shadow-none'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <div className="flex flex-col items-center space-y-0.5">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">Yearly</span>
                    </div>
                    <span className="rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                      2 months for FREE
                    </span>
                    {/* <span className="text-muted-foreground text-xs">
                      Best value
                    </span> */}
                  </div>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {plans.map((plan) => (
                <PricingCard
                  key={plan.name}
                  title={plan.name}
                  price={
                    billingCycle === 'month'
                      ? plan.monthlyPrice
                      : plan.yearlyPrice
                  }
                  description={plan.description}
                  features={plan.features}
                  buttonText={buttonLabel}
                  highlighted={plan.popular}
                  cycle={currentCycle}
                  onClick={() =>
                    handleSubscriptionSelect(plan.name, billingCycle)
                  }
                  isLoading={isLoadingSubscription}
                />
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="credits">
          <div className="flex flex-col space-y-4">
            {creditPacks.map((pack) => (
              <CreditPackCard
                key={pack.name}
                credits={pack.credits}
                price={pack.price}
                standardImages={pack.standardImages}
                aiModels={pack.aiModels}
                onClick={() => handleCreditsSelect(pack.name, pack.credits)}
                isLoading={isLoadingCredits}
              />
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
